import { Component } from 'preact'
import { ParentCategoryInfo } from '../mega-menu/Types'
import LazyLoader from '../lazy-loading/LazyLoader'
import axios from 'axios'
import TrackingLink from '../link/tracking-link'
import { ModulesContext } from '../../utils/globals'
import Phrase from '../../libs/translationsUtils/phrase'
import Icon from '../assets'

interface Props {
  parentCategories: ParentCategoryInfo[]
  collapsed: {
    [key: number]: boolean
  }
  displayUnreadPostCount: boolean
  isDefaultCollapsed: boolean
  unreadTopicsAndRepliesCount: null | {
    [key: string]: {
      topicCount?: number
      postCount?: number
    }
  }
}

interface State {
  activeTab: number
  collapsed: {
    [key: number]: boolean
  }
  customCollapsedCategories: number[]
}

class Categories extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      activeTab: 1,
      collapsed: props.collapsed,
      customCollapsedCategories: Object.keys(props.collapsed).map(id => parseInt(id))
    }
    this.handleCollapse = this.handleCollapse.bind(this)
  }

  async handleCollapse (e: MouseEvent | KeyboardEvent, id: number) {
    e.preventDefault()
    await axios.get('/member/collapsed', {
      params: {
        type: 'categories',
        id
      }
    })

    const newCollapsedList = Object.assign({},
      this.state.collapsed,
      { [id]: this.state.customCollapsedCategories.includes(id) ? !this.state.collapsed[id] : !this.props.isDefaultCollapsed })
    this.setState({
      collapsed: newCollapsedList,
      customCollapsedCategories: Object.keys(newCollapsedList).map(id => parseInt(id))
    })
  }

  render ({ parentCategories, displayUnreadPostCount, unreadTopicsAndRepliesCount, isDefaultCollapsed }: Props, { customCollapsedCategories, collapsed }: State) {
    return (
      <div className='forum-list'>
        {
          parentCategories.map((parentCategory) => {
            const isCollapsed = customCollapsedCategories.includes(parseInt(parentCategory.id)) ? collapsed[parseInt(parentCategory.id)] : isDefaultCollapsed
            return (
              <div className='box' key={`parent-category-${parentCategory.id}`}>
                <div
                  className={`table has--caption qa-category${isCollapsed ? ' is-collapsed' : ''}`}
                  id={`category-${parentCategory.id}`}
                >
                  <div className='table__caption category-title'>
                    <h2>
                      <TrackingLink
                        tag='a'
                        href={parentCategory.url}
                        className='link--subforum qa-category-link'
                        track={{
                          type: 'Category Clicked',
                          category_title: parentCategory.title,
                          category_url: parentCategory.url,
                          click_source: 'Homepage',
                          category_type: 'parent category'
                        }}
                        gaEvents={{
                          eventCategory: 'Homepage',
                          eventAction: 'Category clicked',
                          eventLabel: {
                            Title: parentCategory.title,
                            Link: parentCategory.url,
                            Type: 'parent category'
                          }
                        }}
                      >{parentCategory.title}</TrackingLink>
                    </h2>
                    <span
                      tabIndex={0} role='button' title='forum expand'
                      className='table__collapse collapse-trigger js-collapse-trigger'
                      onClick={async (e) => await this.handleCollapse(e, parseInt(parentCategory.id))}
                      onKeyUp={async (e) => e.code === 'Enter' ? await this.handleCollapse(e, parseInt(parentCategory.id)) : null}
                    >
                      <Icon icon='chevronUp' width={24} height={24} color='currentColor' />
                    </span>
                  </div>
                  {
                    (parentCategory.showDescriptionOnHomepage && parentCategory.description) &&
                      (
                        <div className='category-description text--meta'>{parentCategory.description}</div>)
                  }
                  <div className={`table forum-table js-collapse-content js-unread-overview${isCollapsed ? ' is-hidden' : ''}`}>
                    {
                      parentCategory.subforums.map((category) => (
                        <div className={`table__row qa-table-row${displayUnreadPostCount ? ' js-unread-row' : ''} forum-${category.type} qa-forum--${category.type} `} key={`category-${category.id}`} data-value={category.id}>
                          <div className='table__cell grid--7 cell--S--first'>
                            <div className='user flag'>
                              <div className='flag__image'>
                                <div className='forum-image'>
                                  {
                                      category.thumbnailUrl && (
                                        <LazyLoader
                                          type='img'
                                          src={category.thumbnailUrl}
                                          title={category.title}
                                          alt={category.title}
                                          displayOriginalImage
                                        />
                                      )
                                    }
                                </div>
                              </div>
                              <div className='user_data flag__body'>
                                <h3>
                                  <TrackingLink
                                    tag='a'
                                    href={category.url}
                                    className='link--quiet link--topic link--subforum qa-forum-overview-subforum'
                                    track={{
                                      type: 'Category Clicked',
                                      category_title: category.title,
                                      category_url: category.url,
                                      click_source: 'Homepage',
                                      category_type: 'category'
                                    }}
                                    gaEvents={{
                                      eventCategory: 'Homepage',
                                      eventAction: 'Category clicked',
                                      eventLabel: {
                                        Title: category.title,
                                        Link: category.url,
                                        Type: category.type === 'default' ? 'community' : category.type
                                      }
                                    }}
                                  >{category.title}
                                  </TrackingLink>
                                </h3>
                                <ul className='list list--dot-seperated text--meta m-v-S'>
                                  <li className='qa-forum-overview-topic-counter'>
                                    <Phrase p='forumList.meta.counter.topic' count={{ n: category.topicCount.toString() }} />
                                    {
                                      unreadTopicsAndRepliesCount?.[category.id]?.topicCount !== undefined
                                        ? (
                                          <span className='qa-forum-overview-new-topics'>
                                            (<Phrase p='forumList.meta.counter.topic.user.new' count={{ n: (unreadTopicsAndRepliesCount[category.id]?.topicCount ?? 0).toString() }} />)
                                          </span>
                                          )
                                        : null
                                    }
                                  </li>
                                  <li className='qa-forum-overview-post-counter'>
                                    <Phrase p='forumList.meta.counter.post' count={{ n: category.replyCount.toString() }} />
                                    {
                                      unreadTopicsAndRepliesCount?.[category.id]?.postCount !== undefined
                                        ? (
                                          <span className='qa-forum-overview-new-posts'>
                                            (<Phrase p='forumList.meta.counter.post.user.new' count={{ n: (unreadTopicsAndRepliesCount[category.id]?.postCount || 0).toString() }} />)
                                          </span>
                                          )
                                        : null
                                    }
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            )
          })
        }
        <ModulesContext.Consumer>
          {(modules) => {
            return modules.includes('unreadPostCounter') && displayUnreadPostCount
              ? <div className='box'>
                <a href='/member/markAllForumsAsRead'><Phrase p='Mark forums as read' /></a>
              </div>
              : null
          }}
        </ModulesContext.Consumer>
      </div>
    )
  }
}

export default Categories
